<template>
  <div class="day-editor">
    <div v-if="loading" class="text-center py-4"><div class="spinner-border text-secondary" role="status"></div></div>
    <template v-else>
      <div v-if="plan && plan.isHoliday" class="alert alert-light border text-sm py-2 mb-3">
        <i class="material-icons align-middle me-1" style="font-size:1rem">celebration</i>
        {{ $t('endOfDay.holidayNotice', { name: plan.holidayName }) }}
      </div>
      <div v-else-if="plan && ceiling <= 0 && !plan.absenceHours" class="alert alert-light border text-sm py-2 mb-3">{{ $t('endOfDay.unscheduledNotice') }}</div>
      <div v-if="locked" class="alert alert-warning text-sm py-2 mb-3">{{ $t('endOfDay.periodLocked') }}</div>

      <!-- Absences (editable in a dialog) and other registrations that day (read-only) -->
      <swipe-row
        v-for="e in otherEntries"
        :key="absenceKey(e)"
        class="mb-2"
        :open="swipedKey === absenceKey(e)"
        :disabled="!isMobile || !canEditAbsence(e)"
        @update:open="onSwiped(absenceKey(e), $event)"
      >
        <div
          class="line-row line-row-swipe"
          :class="{ 'line-row-readonly': !canEditAbsence(e) }"
          :role="canEditAbsence(e) ? 'button' : null"
          @click="onAbsenceClick(e)"
        >
          <div class="flex-grow-1 text-truncate">
            <template v-if="e.isAbsence"><span class="text-danger fw-semibold">{{ $t('endOfDay.absence') }}</span> · {{ absenceLabel(e.timesheetEntryType) }}</template>
            <template v-else>{{ projectLabel(e) }}</template>
          </div>
          <span class="fw-bold">{{ fmt(e.isAbsence ? e.absenceHours : entryHours(e)) }}</span>
          <template v-if="canEditAbsence(e) && !isMobile">
            <button class="btn-icon text-secondary ms-2" :title="$t('common.edit')" @click.stop="onAbsenceClick(e, true)"><i class="material-icons">edit</i></button>
            <button class="btn-icon text-danger ms-2" :title="$t('common.delete')" @click.stop="confirmRemoveAbsence(e)"><i class="material-icons">delete</i></button>
          </template>
        </div>
        <template v-if="canEditAbsence(e)" #actions>
          <button class="swipe-action swipe-action-edit" @click="onAbsenceClick(e, true)"><i class="material-icons">edit</i>{{ $t('common.edit') }}</button>
          <button class="swipe-action swipe-action-delete" @click="confirmRemoveAbsence(e)"><i class="material-icons">delete</i>{{ $t('common.delete') }}</button>
        </template>
      </swipe-row>
      <el-dialog :model-value="!!absenceEditor" :title="$t('timeRegistration.editEntryTitle')" width="min(480px, calc(100vw - 24px))" append-to-body destroy-on-close @update:model-value="absenceEditor = null">
        <edit-entry-dialog
          v-if="absenceEditor"
          :entry="absenceEditor"
          :user-id="userId"
          :absence-types="absenceTypes"
          :absence-label="absenceLabel"
          cap-absence
          @saved="onAbsenceEdited"
          @cancel="absenceEditor = null"
        />
      </el-dialog>

      <template v-for="(line, i) in lines" :key="line.key">
        <!-- Collapsed line -->
        <swipe-row v-if="i !== editingIndex" class="mb-2" :open="swipedKey === line.key" :disabled="!isMobile || locked" :peek="swipeHintKey === line.key" @update:open="onSwiped(line.key, $event)" @peek-end="swipeHintKey = null">
          <div class="line-row line-row-swipe" :class="lineRowClass(line)" role="button" @click="onRowClick(i)">
            <div class="flex-grow-1" style="min-width:0">
              <div class="text-truncate">
                <strong v-if="line.projectKey">{{ line.projectKey }}</strong> {{ line.projectName || $t('endOfDay.noProject') }}
              </div>
              <div class="text-xs text-truncate" :class="line.comment.trim() ? 'text-secondary' : 'text-warning'">
                {{ line.comment.trim() || $t('endOfDay.descriptionMissing') }}
              </div>
            </div>
            <div class="text-end text-nowrap">
              <div class="fw-bold">{{ lineHoursLabel(line) }}</div>
              <span v-if="isPending(line)" class="pending-tag">{{ $t('endOfDay.pendingSave') }}</span>
            </div>
            <template v-if="!locked && !isMobile">
              <button class="btn-icon text-secondary ms-2" :title="$t('common.edit')" @click.stop="onRowClick(i)"><i class="material-icons">edit</i></button>
              <button class="btn-icon text-danger ms-2" :title="$t('common.delete')" @click.stop="confirmRemoveLine(i)"><i class="material-icons">delete</i></button>
            </template>
          </div>
          <template #actions>
            <button class="swipe-action swipe-action-edit" @click="onRowClick(i, true)"><i class="material-icons">edit</i>{{ $t('common.edit') }}</button>
            <button class="swipe-action swipe-action-delete" @click="confirmRemoveLine(i)"><i class="material-icons">delete</i>{{ $t('common.delete') }}</button>
          </template>
        </swipe-row>

        <!-- Line being edited -->
        <div v-else class="line-card mb-3">
          <div class="d-flex align-items-center mb-2">
            <span class="section-label">{{ $t('endOfDay.project') }}</span>
            <button v-if="lines.length > 1 || !isBlank(line)" class="btn-icon ms-auto text-secondary" :title="$t('common.close')" @click="closeLine(i)">
              <i class="material-icons">close</i>
            </button>
          </div>

          <div class="position-relative mb-2">
            <i class="material-icons search-icon">search</i>
            <input v-model="search" type="text" class="form-control search-input" :class="{ 'is-invalid': showErrors && lineErrors(line).project }" :placeholder="$t('endOfDay.searchProject')" @input="onSearchInput" />
            <div v-if="search && searchResults.length" class="search-results">
              <button v-for="p in searchResults" :key="projectIdOf(p)" class="search-result" @click="pickProject(line, p)">
                <strong v-if="p.key">{{ p.key }}</strong> {{ p.name }}
              </button>
            </div>
            <div v-else-if="search && !searching" class="search-results text-secondary text-sm p-2">{{ $t('endOfDay.noProjectsFound') }}</div>
          </div>

          <div class="d-flex flex-wrap gap-2 mb-3">
            <button
              v-for="p in projectChips(line)"
              :key="projectIdOf(p)"
              class="btn project-chip mb-0"
              :class="projectIdOf(p) === line.projectId ? 'btn-dark' : 'btn-outline-secondary'"
              @click="pickProject(line, p)"
            >
              {{ p.key ? p.key + ' ' : '' }}{{ p.name }}
            </button>
          </div>
          <p v-if="showErrors && lineErrors(line).project" class="field-error mt-n2 mb-3">{{ $t('endOfDay.chooseProject') }}</p>

          <div v-if="phasesEnabled && line.phases.length" class="mb-3">
            <span class="section-label d-block mb-1">{{ $t('projects.phase') }}</span>
            <select v-model="line.projectPhaseId" class="form-select form-select-sm" :class="{ 'is-invalid': showErrors && lineErrors(line).phase }">
              <option :value="null" :disabled="line.phaseRequired">{{ line.phaseRequired ? $t('projects.selectPhasePlaceholder') : $t('projects.wholeProject') }}</option>
              <option v-for="ph in line.phases" :key="ph.id" :value="ph.id">{{ ph.name }}</option>
            </select>
            <p v-if="showErrors && lineErrors(line).phase" class="field-error mt-1 mb-0">{{ $t('projects.selectPhasePlaceholder') }}</p>
          </div>

          <span class="section-label d-block mb-1">{{ $t('endOfDay.normalHours') }}</span>
          <div class="stepper mb-3" :class="{ 'stepper-invalid': showErrors && lineErrors(line).hours }">
            <button class="btn btn-outline-secondary mb-0" :disabled="line.normalHours <= 0" v-bind="stepButton(line, 'normalHours', -1)">−</button>
            <div class="stepper-value"><span>{{ fmt(line.normalHours) }}</span><small>{{ $t('endOfDay.hoursUnit') }}</small></div>
            <button class="btn btn-outline-secondary mb-0" :disabled="normalRoom() <= 0" v-bind="stepButton(line, 'normalHours', 1)">+</button>
          </div>
          <p v-if="showErrors && lineErrors(line).hours" class="field-error mt-n2 mb-3">{{ $t('endOfDay.registerHours') }}</p>
          <p v-if="normalRoom() <= 0 && ceiling > 0" class="text-xs text-secondary mt-n2 mb-3">{{ $t('endOfDay.normalHoursCapReached', { hours: fmt(ceiling) }) }}</p>

          <div v-if="line.showOvertime" class="overtime-box mb-3">
            <div class="d-flex align-items-center mb-1">
              <span class="section-label overtime-label">{{ $t('endOfDay.overtimeHours') }}</span>
              <button class="btn-icon ms-auto text-secondary" :title="$t('endOfDay.removeOvertime')" @click="line.overtimeHours = 0; line.showOvertime = false">
                <i class="material-icons">close</i>
              </button>
            </div>
            <div class="stepper">
              <button class="btn btn-outline-secondary bg-white mb-0" :disabled="line.overtimeHours <= 0" v-bind="stepButton(line, 'overtimeHours', -1)">−</button>
              <div class="stepper-value overtime-value"><span>{{ fmt(line.overtimeHours) }}</span><small>{{ $t('endOfDay.hoursUnit') }}</small></div>
              <button class="btn btn-outline-secondary bg-white mb-0" :disabled="line.overtimeHours >= maxHours(line, 'overtimeHours')" v-bind="stepButton(line, 'overtimeHours', 1)">+</button>
            </div>
          </div>
          <button v-else class="btn btn-sm btn-link text-warning px-0 mb-3" @click="line.showOvertime = true">
            <i class="material-icons align-middle me-1" style="font-size:1rem">add</i>{{ $t('endOfDay.addOvertime') }}
          </button>

          <span class="section-label d-block mb-1">{{ $t('endOfDay.description') }} *</span>
          <comment-suggest-input v-model="line.comment" :user-id="userId" :placeholder="$t('endOfDay.descriptionPlaceholder')" :invalid="showErrors && lineErrors(line).comment" />
          <p v-if="showErrors && lineErrors(line).comment" class="field-error mt-1 mb-0">{{ $t('endOfDay.descriptionRequired') }}</p>

          <template v-if="referenceEnabled">
            <span class="section-label d-block mt-3 mb-1">{{ $t('endOfDay.reference') }}</span>
            <comment-suggest-input v-model="line.reference" field="reference" class="reference-input" :user-id="userId" />
          </template>
        </div>
      </template>

      <!-- Progress against the schedule: dagvinna | yfirvinna -->
      <div class="progress-wrap mb-1">
        <div class="progress-seg" :style="{ flex: normalSegFlex }">
          <div class="progress-fill" :class="{ complete: dayComplete }" :style="{ width: pct(totals.normal, scheduledNormal) }"></div>
        </div>
        <div v-if="scheduledOvertime > 0 || totals.overtime > 0" class="progress-seg" :style="{ flex: overtimeSegFlex }">
          <div class="progress-fill overtime" :class="{ complete: dayComplete }" :style="{ width: pct(totals.overtime, scheduledOvertime) }"></div>
        </div>
      </div>
      <div class="d-flex justify-content-between text-sm fw-semibold mb-3">
        <span>{{ fmt(totals.normal) }}</span>
        <span v-if="scheduledOvertime > 0 || totals.overtime > 0" class="text-warning">{{ fmt(totals.overtime) }}</span>
      </div>

      <button class="btn btn-dark w-100 save-day-btn" :class="{ 'save-blocked': !canSave }" :disabled="saving || locked" @click="onSaveClick">
        <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>{{ $t('endOfDay.saveDay') }}
      </button>
      <p v-if="saveBlockReason" class="text-xs text-center mt-2 mb-0" :class="showErrors ? 'field-error' : 'text-secondary'">{{ saveBlockReason }}</p>

      <div class="text-center mt-3">
        <button class="btn btn-link text-dark fw-semibold mb-0" :disabled="locked || lines.length >= maxLines" @click="addLine">
          + {{ $t('endOfDay.addLine') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import api from "../../services/api";
import { ElDialog, ElMessageBox } from "element-plus";
import CommentSuggestInput, { clearCommentSuggestions } from "./CommentSuggestInput.vue";
import EditEntryDialog from "./EditEntryDialog.vue";
import SwipeRow from "./SwipeRow.vue";
import { createHoldStepper, nextHours } from "./hoursStepper";

const MAX_LINES = 20;
const MAX_DAY_HOURS = 24; // same limit as the server
const SWIPE_HINT_STORAGE_KEY = "timeRegistration.swipeHintsShown";
const MAX_SWIPE_HINTS = 3; // also what the first real swipe sets the count to: no more hints after it
let swipeHintPlayed = false; // once per page load, not on every day the user browses to
let lineKey = 0;

function emptyLine(normalHours = 0, overtimeHours = 0) {
  return {
    key: ++lineKey, id: null, projectId: null, projectKey: "", projectName: "",
    projectPhaseId: null, phases: [], phaseRequired: false,
    normalHours, overtimeHours, showOvertime: overtimeHours > 0, comment: "", reference: "",
    saved: null, // the values last saved, to tell when the line has changes pending
  };
}

function lineSnapshot(l) {
  return JSON.stringify([l.projectId, l.projectPhaseId, l.normalHours, l.overtimeHours, l.comment.trim(), l.reference.trim()]);
}

/**
 * DayRegistrationEditor — end-of-day registration of one date (EnableEndOfDayTimeRegistration).
 * Lines are project + dagvinna/yfirvinna + required description, saved together through
 * PUT api/Timesheet/day. The dagvinna ceiling, scheduled hours and holiday flag come from
 * GET api/Timesheet/day-plan so the rules live on the server. Used by the worker's Skrá dag
 * screen and by the admin overview (userId set) to correct a worker's day.
 */
export default {
  name: "DayRegistrationEditor",
  components: { CommentSuggestInput, EditEntryDialog, ElDialog, SwipeRow },
  props: {
    date: { type: String, required: true }, // yyyy-mm-dd
    userId: { type: String, default: null }, // admin editing another user's day
    locked: { type: Boolean, default: false },
    phasesEnabled: { type: Boolean, default: false },
    referenceEnabled: { type: Boolean, default: false },
  },
  emits: ["saved"],
  data() {
    return {
      loading: true,
      loadId: 0, // the latest load: a slower earlier one is dropped
      saving: false,
      plan: null,
      lines: [],
      otherEntries: [],
      editingIndex: 0,
      swipedKey: null, // the line whose actions are showing
      swipeHintKey: null, // the line playing the swipe hint
      isMobile: false, // swipe for the row actions below this width, buttons on the row above it
      recentProjects: [],
      search: "",
      searchResults: [],
      searching: false,
      searchTimer: null,
      absenceTypeLabels: {},
      absenceTypes: [],
      absenceEditor: null, // the absence being edited in the dialog
      savedLineCount: 0,
      phaseCache: {},
      maxLines: MAX_LINES,
      showErrors: false, // set by a click on Vista while something is missing
      hold: createHoldStepper(),
    };
  },
  computed: {
    ceiling() { return this.plan ? Number(this.plan.normalHoursCeiling) || 0 : 0; },
    scheduledNormal() { return this.plan ? Math.max(0, (Number(this.plan.scheduledNormalHours) || 0) - (Number(this.plan.absenceHours) || 0)) : 0; },
    scheduledOvertime() { return this.plan ? Number(this.plan.scheduledOvertimeHours) || 0 : 0; },
    totals() {
      return this.lines.reduce((t, l) => ({ normal: t.normal + l.normalHours, overtime: t.overtime + l.overtimeHours }), { normal: 0, overtime: 0 });
    },
    dayComplete() {
      const scheduled = this.scheduledNormal + this.scheduledOvertime;
      return scheduled > 0 ? this.totals.normal + this.totals.overtime >= scheduled : this.totals.normal + this.totals.overtime > 0;
    },
    normalSegFlex() { return Math.max(this.scheduledNormal, this.totals.normal, 1); },
    overtimeSegFlex() { return Math.max(this.scheduledOvertime, this.totals.overtime, 0.5); },
    hasPendingChanges() { return !this.loading && this.lines.some(l => this.isPending(l)); },
    /** Lines that go to the server — an untouched blank line (no project, no description) is left out. */
    linesToSave() { return this.lines.filter(l => !this.isBlank(l)); },
    saveBlockReason() {
      if (this.locked) return "";
      // Nothing left on a day that had lines = clear the day; nothing at all = nothing to save
      if (this.linesToSave.length === 0) return this.savedLineCount > 0 ? "" : this.$t("endOfDay.chooseProject");
      const normal = this.linesToSave.reduce((s, l) => s + l.normalHours, 0);
      if (normal > this.ceiling + 1e-9) return this.$t("endOfDay.normalHoursCapReached", { hours: this.fmt(this.ceiling) });
      for (const l of this.linesToSave) {
        if (!l.projectId) return this.$t("endOfDay.chooseProject");
        if (l.normalHours + l.overtimeHours <= 0) return this.$t("endOfDay.registerHours");
        if (!l.comment.trim()) return this.$t("endOfDay.descriptionRequired");
        if (this.phasesEnabled && l.phaseRequired && l.phases.length && !l.projectPhaseId) return this.$t("projects.selectPhasePlaceholder");
      }
      return "";
    },
    canSave() { return !this.saveBlockReason; },
    /** Lines the save checks: the ones going to the server, or the first blank line on an empty day. */
    checkedLines() {
      if (this.linesToSave.length) return this.linesToSave;
      return this.savedLineCount > 0 ? [] : this.lines.slice(0, 1);
    },
  },
  watch: {
    date() { this.load(); },
    userId() { this.load(); },
  },
  created() {
    this.mobileQuery = window.matchMedia("(max-width: 767px)");
    this.mobileQuery.addEventListener("change", this.onViewportChange);
    this.onViewportChange();
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    this.hold.stop();
    this.mobileQuery.removeEventListener("change", this.onViewportChange);
  },
  methods: {
    projectIdOf(p) { return p?.id?.value ?? p?.id ?? null; },
    entryProjectId(e) { return e.projectId?.value ?? e.projectId ?? null; },
    isHoursOnly(e) { return !e.isAbsence && !e.timeRegistrationGroupId && !!this.entryProjectId(e) && !e.realClockIn; },
    entryHours(e) {
      if (!e.realClockIn) return (Number(e.normalHours) || 0) + (Number(e.overtimeHours) || 0) + (Number(e.afterHours) || 0) + (Number(e.redDayHours) || 0);
      return (e.adjustedDurationMinutes || 0) / 60;
    },
    projectLabel(e) {
      if (e.projectName) return `${e.projectKey ? e.projectKey + " " : ""}${e.projectName}`;
      return e.timeRegistrationGroupName || "";
    },
    absenceLabel(type) { return this.absenceTypeLabels[type] || type; },
    canEditAbsence(e) { return e.isAbsence && !this.locked; },
    absenceKey(e) { return `o${e.id}`; },
    onAbsenceClick(e, fromAction = false) {
      if (!this.canEditAbsence(e)) return;
      const wasOpen = this.swipedKey === this.absenceKey(e);
      this.swipedKey = null;
      if (fromAction || !wasOpen) this.absenceEditor = e;
    },
    /** An edited absence changes the dagvinna ceiling: refresh the plan and absences, keeping unsaved work lines. */
    async onAbsenceEdited() {
      this.absenceEditor = null;
      this.$emit("saved");
      const userParam = this.userId ? `&userId=${this.userId}` : "";
      try {
        const [planRes, entriesRes] = await Promise.all([
          api.get(`/Timesheet/day-plan?dateFrom=${this.date}&dateTo=${this.date}${userParam}`),
          api.get(`/Timesheet?dateFrom=${this.date}&dateTo=${this.date}${userParam}`),
        ]);
        this.plan = (planRes.data || [])[0] || null;
        this.otherEntries = (entriesRes.data?.days || []).flatMap(d => d.entries || []).filter(e => !this.isHoursOnly(e));
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    fmt(h) {
      const v = Number(h) || 0;
      return v.toLocaleString(this.$i18n.locale?.value || this.$i18n.locale || "is-IS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    pct(value, scheduled) {
      if (scheduled <= 0) return value > 0 ? "100%" : "0%";
      return `${Math.min(100, (value / scheduled) * 100)}%`;
    },
    lineHoursLabel(line) {
      if (line.normalHours === 0 && line.overtimeHours > 0) return `${this.fmt(line.overtimeHours)} ${this.$t("endOfDay.overtimeShort")}`;
      return line.overtimeHours > 0 ? `${this.fmt(line.normalHours)} + ${this.fmt(line.overtimeHours)}` : this.fmt(line.normalHours);
    },
    onViewportChange() {
      this.isMobile = this.mobileQuery.matches;
      this.swipedKey = null;
    },
    isBlank(line) { return !line.id && !line.projectId && !line.comment.trim() && !line.reference.trim(); },
    isPending(line) { return !this.isBlank(line) && lineSnapshot(line) !== line.saved; },
    lineRowClass(line) {
      return {
        "line-row-invalid": this.showErrors && this.lineHasErrors(line),
        "line-row-pending": this.isPending(line),
      };
    },
    onRowClick(i, fromAction = false) {
      const line = this.lines[i];
      if (this.locked) return;
      const wasOpen = this.swipedKey === line.key;
      this.swipedKey = null;
      if (fromAction || !wasOpen) this.editingIndex = i;
    },
    onSwiped(key, open) {
      this.swipedKey = open ? key : null;
      if (open) try { localStorage.setItem(SWIPE_HINT_STORAGE_KEY, MAX_SWIPE_HINTS); } catch { /* ignore */ } // eslint-disable-line no-empty
    },
    /** Slides the first row aside and back so people on phones find the actions behind it. */
    showSwipeHint() {
      if (swipeHintPlayed || !this.isMobile || this.locked || this.editingIndex !== -1) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      try {
        const shown = Number(localStorage.getItem(SWIPE_HINT_STORAGE_KEY)) || 0;
        if (shown >= MAX_SWIPE_HINTS) return;
        localStorage.setItem(SWIPE_HINT_STORAGE_KEY, shown + 1);
      } catch {
        return;
      }
      swipeHintPlayed = true;
      this.swipeHintKey = this.lines[0].key;
    },
    /** What is missing on a line (only for lines the save checks). */
    lineErrors(line) {
      if (!this.checkedLines.includes(line)) return {};
      return {
        project: !line.projectId,
        phase: !!(this.phasesEnabled && line.projectId && line.phaseRequired && line.phases.length && !line.projectPhaseId),
        hours: line.normalHours + line.overtimeHours <= 0,
        comment: !line.comment.trim(),
      };
    },
    lineHasErrors(line) { return Object.values(this.lineErrors(line)).some(Boolean); },
    /** Vista while something is missing: mark the missing fields and open the first line that needs work. */
    onSaveClick() {
      if (this.canSave) { this.save(); return; }
      this.showErrors = true;
      const i = this.lines.findIndex(l => this.lineHasErrors(l));
      if (i >= 0) this.editingIndex = i;
    },
    /** Dagvinna this line can still take before the day's ceiling. */
    normalRoom() { return this.ceiling - this.totals.normal; },
    /** Highest value the line can take, on the quarter-hour grid the server requires. */
    maxHours(line, field) {
      const room = field === "normalHours" ? this.normalRoom() : MAX_DAY_HOURS - this.totals.normal - this.totals.overtime;
      return Math.floor((line[field] + room) * 4) / 4;
    },
    /** Returns whether the value moved. */
    step(line, field, direction, held) {
      const next = nextHours(line[field], direction, this.maxHours(line, field), { held, quarters: field === "overtimeHours" });
      const moved = next !== line[field];
      line[field] = next;
      return moved;
    },
    stepButton(line, field, direction) {
      return this.hold.button(held => this.step(line, field, direction, held));
    },
    projectChips(line) {
      const chips = [...this.recentProjects];
      if (line.projectId && !chips.some(p => this.projectIdOf(p) === line.projectId)) {
        chips.unshift({ id: line.projectId, key: line.projectKey, name: line.projectName });
      }
      return chips;
    },

    async load() {
      const loadId = ++this.loadId;
      this.loading = true;
      this.showErrors = false;
      const userParam = this.userId ? `&userId=${this.userId}` : "";
      try {
        const [planRes, entriesRes, recentRes, typesRes] = await Promise.all([
          api.get(`/Timesheet/day-plan?dateFrom=${this.date}&dateTo=${this.date}${userParam}`),
          api.get(`/Timesheet?dateFrom=${this.date}&dateTo=${this.date}${userParam}`),
          api.get("/Project?currentPage=1&pageSize=2&projectStatusId=2&orderByLatestUsedInTimeRegistration=true"),
          api.get("/Timesheet/types"),
        ]);
        if (loadId !== this.loadId) return; // another day was picked meanwhile: its lines must not land on that day
        this.plan = (planRes.data || [])[0] || null;
        this.recentProjects = recentRes.data?.result || [];
        this.absenceTypes = (typesRes.data || []).filter(t => t.isAbsence);
        this.absenceTypeLabels = Object.fromEntries((typesRes.data || []).map(t => {
          const key = `timesheetEntryTypes.${t.type.charAt(0).toLowerCase()}${t.type.slice(1)}`;
          return [t.type, this.$te(key) ? this.$t(key) : t.type];
        }));
        const entries = (entriesRes.data?.days || []).flatMap(d => d.entries || []);
        this.otherEntries = entries.filter(e => !this.isHoursOnly(e));
        this.lines = entries.filter(e => this.isHoursOnly(e)).map(e => {
          const line = {
            ...emptyLine(Number(e.normalHours) || 0, Number(e.overtimeHours) || 0),
            id: e.id,
            projectId: this.entryProjectId(e),
            projectKey: e.projectKey || "",
            projectName: e.projectName || "",
            projectPhaseId: e.projectPhaseId ?? null,
            comment: e.comment || "",
            reference: e.reference || "",
          };
          line.saved = lineSnapshot(line);
          return line;
        });
        this.savedLineCount = this.lines.length;
        this.swipedKey = null;
        if (this.lines.length === 0) {
          // Pre-fill from the schedule: e.g. 7,75 dagvinna + 0,75 yfirvinna
          this.lines = [this.newLine(this.ceiling, this.scheduledOvertime)];
          this.editingIndex = 0;
        } else {
          this.editingIndex = -1;
        }
        if (this.phasesEnabled) await Promise.all(this.lines.filter(l => l.projectId).map(l => this.loadPhases(l)));
        this.showSwipeHint();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.$toast?.danger(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        if (loadId === this.loadId) this.loading = false;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      const q = this.search.trim();
      if (!q) { this.searchResults = []; return; }
      this.searching = true;
      this.searchTimer = setTimeout(async () => {
        try {
          const r = await api.get(`/Project?currentPage=1&pageSize=10&projectStatusId=2&searchString=${encodeURIComponent(q)}`);
          if (this.search.trim() === q) this.searchResults = r.data?.result || [];
        } catch (e) { console.error(e); } finally { this.searching = false; } // eslint-disable-line no-console
      }, 300);
    },
    async pickProject(line, p) {
      line.projectId = this.projectIdOf(p);
      line.projectKey = p.key || "";
      line.projectName = p.name || "";
      line.projectPhaseId = null;
      line.phases = [];
      line.phaseRequired = !!p.phaseRequired;
      this.search = "";
      this.searchResults = [];
      if (this.phasesEnabled && (p.phasesCount == null || p.phasesCount > 0)) {
        await this.loadPhases(line);
        // Quick-select chips carry the phase the user last registered on the project
        if (p.lastUsedPhaseId && line.phases.some(ph => ph.id === p.lastUsedPhaseId)) line.projectPhaseId = p.lastUsedPhaseId;
      }
    },
    /** Phases of the line's project — fetched once per project, shared by its lines. */
    async loadPhases(line) {
      const id = line.projectId;
      this.phaseCache[id] ||= api.get(`/Project/${id}/Phases`).then(r => r.data || []).catch(e => { console.error(e); delete this.phaseCache[id]; return []; }); // eslint-disable-line no-console
      line.phases = await this.phaseCache[id];
    },
    /** A new line pre-filled with what is left of the schedule; on days without dagvinna it opens on yfirvinna. */
    newLine(normal, overtime) {
      const line = emptyLine(normal, overtime);
      if (this.ceiling <= 0) line.showOvertime = true;
      return line;
    },
    addLine() {
      const normal = Math.max(0, this.ceiling - this.totals.normal);
      const overtime = Math.max(0, this.scheduledOvertime - this.totals.overtime);
      this.lines.push(this.newLine(normal, overtime));
      this.editingIndex = this.lines.length - 1;
    },
    /** The X on an open line only closes it: changes stay on the row, pending Vista. */
    closeLine(i) {
      this.search = "";
      this.searchResults = [];
      const blank = this.isBlank(this.lines[i]);
      this.editingIndex = -1;
      if (blank) this.lines.splice(i, 1);
    },
    /** Before anything that replaces the lines. Returns whether that may go ahead. */
    async confirmDiscardChanges() {
      if (!this.hasPendingChanges) return true;
      try {
        await ElMessageBox.confirm(this.$t("endOfDay.unsavedMessage"), this.$t("endOfDay.unsavedTitle"), {
          confirmButtonText: this.$t("endOfDay.discardChanges"),
          cancelButtonText: this.$t("endOfDay.keepEditing"),
          type: "warning",
        });
        return true;
      } catch {
        return false; // stay on the day
      }
    },
    /** Returns whether the user confirmed. */
    async confirmDelete(project, irreversible) {
      const question = this.$t("endOfDay.deleteLineConfirm", { project });
      try {
        await ElMessageBox.confirm(irreversible ? `${question} ${this.$t("endOfDay.deleteLineIrreversible")}` : question, this.$t("endOfDay.deleteLineTitle"), {
          confirmButtonText: this.$t("common.delete"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning",
        });
        return true;
      } catch {
        return false; // cancelled
      }
    },
    async confirmRemoveLine(i) {
      const line = this.lines[i];
      const project = `${line.projectKey} ${line.projectName}`.trim() || this.$t("endOfDay.noProject");
      if (!(await this.confirmDelete(project, !!line.id))) return;
      if (line.id) {
        if (!(await this.deleteEntry(line.id))) return;
        this.savedLineCount--;
        clearCommentSuggestions();
        this.$emit("saved");
      }
      this.removeLine(this.lines.indexOf(line));
    },
    async confirmRemoveAbsence(e) {
      if (!(await this.confirmDelete(`${this.$t("endOfDay.absence")} · ${this.absenceLabel(e.timesheetEntryType)}`, true))) return;
      if (!(await this.deleteEntry(e.id))) return;
      this.swipedKey = null;
      await this.onAbsenceEdited();
    },
    /** Deletes just this entry on the server: unsaved changes on the lines stay pending. */
    async deleteEntry(id) {
      this.saving = true;
      try {
        await api.delete(`/Timesheet/${id}`);
        return true;
      } catch (e) {
        this.showRequestError(e);
        return false;
      } finally {
        this.saving = false;
      }
    },
    removeLine(i) {
      this.swipedKey = null;
      this.lines.splice(i, 1);
      if (this.lines.length === 0) {
        this.lines.push(this.newLine(this.ceiling, this.scheduledOvertime));
        this.editingIndex = 0;
      } else if (this.editingIndex > i) {
        this.editingIndex--;
      }
    },
    async save() {
      this.saving = true;
      try {
        await api.put("/Timesheet/day", {
          date: this.date,
          ...(this.userId ? { userId: this.userId } : {}),
          entries: this.linesToSave.map(l => ({
            id: l.id,
            projectId: l.projectId,
            projectPhaseId: l.projectPhaseId,
            normalHours: l.normalHours,
            overtimeHours: l.overtimeHours,
            comment: l.comment.trim(),
            reference: l.reference.trim(),
          })),
        });
        this.$toast?.success(this.$t("endOfDay.daySaved"), { timeout: 2500 });
        clearCommentSuggestions();
        this.$emit("saved");
        await this.load();
      } catch (e) {
        this.showRequestError(e);
      } finally {
        this.saving = false;
      }
    },
    showRequestError(e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.errors?.join?.(" ") || e?.response?.data || this.$t("common.unableToUpdate");
      this.$toast?.danger(typeof msg === "string" ? msg : JSON.stringify(msg), { timeout: 0 });
      console.error(e); // eslint-disable-line no-console
    },
  },
};
</script>

<style scoped>
.section-label { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #67748e; }
.line-card { border: 2px solid #344767; border-radius: 0.75rem; padding: 1rem; }
.line-row { display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 0.9rem; border-radius: 0.5rem; background: #f6f7fb; cursor: pointer; }
.line-row-readonly { cursor: default; }
.line-row-swipe { border-radius: 0; } /* SwipeRow rounds the corners; the actions must not show through here */
.search-icon { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: #67748e; font-size: 1.2rem; }
.reference-input { max-width: 9rem; }
.search-input { padding-left: 2.4rem !important; border: 1px solid #d2d6da !important; border-radius: 0.5rem; }
.search-results { position: absolute; z-index: 10; left: 0; right: 0; top: 100%; background: #fff; border: 1px solid #d2d6da; border-radius: 0.5rem; max-height: 260px; overflow-y: auto; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); }
.search-result { display: block; width: 100%; text-align: left; border: 0; background: none; padding: 0.5rem 0.75rem; font-size: 0.875rem; }
.search-result:hover { background: #f0f2f5; }
.project-chip { border-radius: 2rem; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-transform: none; font-weight: 600; }
.stepper { display: flex; align-items: center; gap: 0.4rem; }
.stepper .btn { flex: 1 1 0; padding: 0.5rem 0; font-size: 1.75rem; font-weight: 600; line-height: 1.3; text-transform: none; touch-action: manipulation; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; }
.stepper-value { flex: 1.4 1 0; display: flex; flex-direction: column; align-items: center; line-height: 1; }
.stepper-value span { font-size: 1.75rem; font-weight: 700; color: #344767; }
.stepper-value small { font-size: 0.75rem; color: #67748e; }
.overtime-box { background: #fff8e6; border: 1px solid #f5d98b; border-radius: 0.6rem; padding: 0.75rem; }
.overtime-label, .overtime-value span { color: #8a5a00; }
.overtime-value { flex-direction: row; align-items: baseline; justify-content: center; gap: 0.25rem; }
.progress-wrap { display: flex; gap: 4px; height: 8px; }
.progress-seg { background: #e4e7ee; border-radius: 4px; overflow: hidden; }
.progress-fill { height: 100%; background: #8392ab; transition: width 0.2s; }
.progress-fill.overtime { background: #e0b04c; }
.progress-fill.complete { background: #2dce89; }
.save-day-btn { padding: 0.9rem; font-size: 1rem; font-weight: 700; letter-spacing: 0.04em; }
.btn-icon { background: none; border: 0; padding: 0; line-height: 1; }
.line-row-pending { box-shadow: inset 3px 0 0 #e0b04c; }
.line-row-invalid { box-shadow: inset 0 0 0 2px #ea0606; }
.pending-tag { display: inline-block; padding: 0 0.4rem; border-radius: 1rem; font-size: 0.65rem; font-weight: 700; color: #8a5a00; background: #fff3cd; }
.swipe-action { flex: 1 1 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.1rem; min-width: 0; border: 0; color: #fff; font-size: 0.7rem; font-weight: 600; }
.swipe-action .material-icons { font-size: 1.2rem; }
.swipe-action-edit { background: #344767; }
.swipe-action-delete { background: #ea0606; }
.is-invalid { border-color: #ea0606 !important; }
.stepper-invalid .stepper-value span { color: #ea0606; }
.field-error { font-size: 0.75rem; font-weight: 600; color: #ea0606; }
.save-blocked { opacity: 0.55; }
</style>
